(() => {
  const initialiseFeed = (section) => {
    if (!section || section.dataset.ugcReady === 'true') return;
    section.dataset.ugcReady = 'true';
    const track = section.querySelector('[data-ugc-track]');
    const cards = [...section.querySelectorAll('[data-ugc-card]')];
    const dotsHost = section.querySelector('[data-ugc-dots]');
    if (!track || !cards.length) return;

    const videos = cards.map((card) => card.querySelector('video')).filter(Boolean);
    const pauseAllExcept = (current) => videos.forEach((video) => video !== current && video.pause());
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      const video = entry.target.querySelector('video');
      if (!video) return;
      if (entry.isIntersecting && entry.intersectionRatio >= .62) {
        pauseAllExcept(video);
        video.play().catch(() => {});
      } else video.pause();
    }), { root: track, threshold: [.2, .62] });
    cards.forEach((card) => observer.observe(card));

    section.querySelectorAll('[data-ugc-sound]').forEach((button) => button.addEventListener('click', () => {
      const video = button.closest('[data-ugc-card]')?.querySelector('video');
      if (!video) return;
      video.muted = !video.muted;
      button.classList.toggle('is-unmuted', !video.muted);
      button.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      if (video.paused) video.play().catch(() => {});
    }));

    let active = 0;
    let maxIndex = cards.length - 1;
    const dots = [];
    const updatePositions = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      maxIndex = cards.reduce((best, card, index) => Math.abs(card.offsetLeft - maxScroll) < Math.abs(cards[best].offsetLeft - maxScroll) ? index : best, 0);
      if (dotsHost) {
        dotsHost.innerHTML = '';
        dots.length = 0;
        for (let i = 0; i <= maxIndex; i += 1) {
          const dot = document.createElement('i');
          dotsHost.append(dot); dots.push(dot);
        }
      }
      setActive(Math.min(active, maxIndex));
    };
    const setActive = (index) => { active = index; dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active)); };
    const goTo = (index) => {
      const count = maxIndex + 1;
      const target = (index + count) % count;
      track.scrollTo({ left: cards[target].offsetLeft, behavior: 'smooth' });
      setActive(target);
    };
    section.querySelector('[data-ugc-prev]')?.addEventListener('click', () => goTo(active - 1));
    section.querySelector('[data-ugc-next]')?.addEventListener('click', () => goTo(active + 1));
    let scrollTimer;
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const reachable = cards.slice(0, maxIndex + 1);
        const closest = reachable.reduce((best, card, index) => Math.abs(card.offsetLeft - track.scrollLeft) < best.distance ? { index, distance: Math.abs(card.offsetLeft - track.scrollLeft) } : best, { index: 0, distance: Infinity });
        setActive(closest.index);
      }, 80);
    }, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(updatePositions).observe(track);
    requestAnimationFrame(updatePositions);
  };

  const initialisePopup = (popup) => {
    if (!popup || popup.dataset.ugcPopupReady === 'true') return;
    popup.dataset.ugcPopupReady = 'true';
    const video = popup.querySelector('video');
    const storageKey = `ft-ugc-closed-${popup.dataset.productId || 'product'}`;
    if (sessionStorage.getItem(storageKey) === 'true') return;
    const show = () => { popup.classList.add('is-visible'); video?.play().catch(() => {}); };
    window.setTimeout(show, Number(popup.dataset.delay || 1800));
    const showReopenPill = () => {
      if (document.querySelector('.ft-video-reopen-pill')) return;
      const reopen = document.createElement('button');
      reopen.className = 'ft-video-reopen-pill';
      reopen.type = 'button';
      reopen.setAttribute('aria-label', 'Watch video');
      reopen.innerHTML = `
        <span class="ft-video-reopen-pill__icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg></span>
        <span class="ft-video-reopen-pill__text">
          <strong>Watch video</strong>
          <small>Tap to play</small>
        </span>
      `;
      reopen.addEventListener('click', () => {
        reopen.remove();
        popup.classList.add('is-visible');
        video?.play().catch(() => {});
      });
      document.body.append(reopen);
    };

    popup.querySelector('[data-ugc-popup-close]')?.addEventListener('click', () => {
      popup.classList.remove('is-visible');
      video?.pause();
      sessionStorage.setItem(storageKey, 'true');
      showReopenPill();
    });
    popup.querySelector('[data-ugc-popup-sound]')?.addEventListener('click', (event) => {
      if (!video) return;
      video.muted = !video.muted;
      event.currentTarget.textContent = video.muted ? '⌁' : '♪';
      event.currentTarget.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      video.play().catch(() => {});
    });
    document.addEventListener('visibilitychange', () => document.hidden ? video?.pause() : popup.classList.contains('is-visible') && video?.play().catch(() => {}));
  };

  const initialisePopupLoader = async (loader) => {
    if (!loader || loader.dataset.ugcLoaderReady === 'true') return;
    loader.dataset.ugcLoaderReady = 'true';
    try {
      const root = window.Shopify?.routes?.root || '/';
      const homeURL = new URL(root, window.location.origin);
      const currentParams = new URLSearchParams(window.location.search);
      ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
        if (currentParams.has(key)) homeURL.searchParams.set(key, currentParams.get(key));
      });
      const response = await fetch(homeURL.toString(), { credentials: 'same-origin' });
      if (!response.ok) return;
      const html = await response.text();
      const source = new DOMParser().parseFromString(html, 'text/html');
      const card = source.querySelector(`[data-ugc-product-id="${CSS.escape(loader.dataset.productId)}"]`);
      const sourceVideo = card?.querySelector('video');
      if (!sourceVideo) return;

      const popup = document.createElement('aside');
      popup.className = 'ft-ugc-popup';
      popup.dataset.ftUgcPopup = '';
      popup.dataset.productId = loader.dataset.productId;
      popup.dataset.delay = loader.dataset.delay || '1800';
      popup.style.setProperty('--ugc-popup-width', `${loader.dataset.popupWidth || 220}px`);
      popup.setAttribute('aria-label', `Video featuring ${loader.dataset.productTitle || 'this product'}`);
      const frame = document.createElement('div');
      frame.className = 'ft-ugc-popup__frame';
      const video = sourceVideo.cloneNode(true);
      video.muted = true; video.loop = true; video.autoplay = true; video.controls = false;
      const sound = document.createElement('button');
      sound.className = 'ft-ugc-popup__sound'; sound.type = 'button'; sound.dataset.ugcPopupSound = ''; sound.setAttribute('aria-label', 'Unmute video'); sound.textContent = '⌁';
      const close = document.createElement('button');
      close.className = 'ft-ugc-popup__close'; close.type = 'button'; close.dataset.ugcPopupClose = ''; close.setAttribute('aria-label', 'Close video'); close.textContent = '×';
      const caption = document.createElement('div');
      caption.className = 'ft-ugc-popup__caption'; caption.textContent = `Featured with ${loader.dataset.productTitle || 'this product'}`;
      frame.append(video, sound, close, caption); popup.append(frame); document.body.append(popup);
      initialisePopup(popup);
    } catch (error) {
      // Keep product pages functional if the homepage section is unavailable.
    }
  };

  const boot = (root = document) => {
    root.querySelectorAll('[data-ft-ugc]').forEach(initialiseFeed);
    root.querySelectorAll('[data-ft-ugc-popup]').forEach(initialisePopup);
    root.querySelectorAll('[data-ft-ugc-popup-loader]').forEach(initialisePopupLoader);
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => boot()) : boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
